import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createNews, updateNews, getNews, uploadImage, newsImageUrl, CATEGORIES } from "../api/news";
import { Button, Skeleton } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/news.css";

const MIN_USER_ID = 1;
const BLOCK_TYPES = [
  { type: "paragraph", label: "¶ Параграф" },
  { type: "heading", label: "H Заголовок" },
  { type: "image", label: "🖼 Картинка" },
  { type: "list", label: "• Список" },
  { type: "quote", label: "❝ Цитата" },
];

export default function NewsEditorPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle(editing ? "Редактировать новость" : "Новая новость");

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("update");
  const [cover, setCover] = useState(null);
  const [isPinned, setIsPinned] = useState(false);
  const [blocks, setBlocks] = useState([{ type: "paragraph", text: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Доступ только МИНу
  useEffect(() => {
    if (user && user.userId !== MIN_USER_ID) navigate("/app/news");
  }, [user, navigate]);

  /* Загрузка редактируемой новости.
     БЫЛ `.catch(() => {})`: если запрос падал, открывалась ПУСТАЯ форма —
     и «Сохранить» затирало живую статью пустышкой. Потеря данных из-за
     одной проглоченной ошибки. Теперь до успешной загрузки сохранение
     заблокировано, а ошибка видна и её можно повторить. */
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [loadTick, setLoadTick] = useState(0);

  useEffect(() => {
    if (!editing) return undefined;
    let cancelled = false;
    setLoadingNews(true);
    setLoadFailed(false);
    getNews(id)
      .then((n) => {
        if (cancelled) return;
        setTitle(n.title); setSummary(n.summary || ""); setCategory(n.category);
        setCover(n.cover_image); setIsPinned(n.is_pinned);
        setBlocks(n.content_blocks?.length ? n.content_blocks : [{ type: "paragraph", text: "" }]);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoadingNews(false); });
    return () => { cancelled = true; };
  }, [editing, id, loadTick]);

  function setBlock(i, patch) {
    setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  }
  function addBlock(type) {
    const base = type === "list" ? { type, items: [""] } : { type, text: "" };
    setBlocks((bs) => [...bs, base]);
  }
  function removeBlock(i) { setBlocks((bs) => bs.filter((_, j) => j !== i)); }
  function moveBlock(i, dir) {
    setBlocks((bs) => {
      const j = i + dir;
      if (j < 0 || j >= bs.length) return bs;
      const copy = [...bs]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
    });
  }

  /* Загрузка картинки шла без индикатора: человек выбирал файл, и
     несколько секунд не происходило ничего видимого. */
  const [uploading, setUploading] = useState(false);
  async function onUpload(file, cb) {
    setUploading(true);
    setErr(null);
    try { const r = await uploadImage(file); cb(r.url); }
    catch { setErr("Не удалось загрузить картинку"); }
    finally { setUploading(false); }
  }

  async function save(publish) {
    /* Защита от перезаписи: если исходную новость загрузить не удалось,
       форма пуста не потому, что статья пустая. Сохранять нельзя. */
    if (editing && (loadFailed || loadingNews)) {
      setErr("Сначала дождись загрузки новости — иначе сохранение затрёт её.");
      return;
    }
    const t = title.trim();
    if (!t) { setErr("Введите заголовок"); return; }
    setBusy(true); setErr(null);
    const payload = {
      title: t, summary, category, cover_image: cover, is_pinned: isPinned,
      content_blocks: blocks.filter((b) =>
        (b.text && b.text.trim()) || (b.url) || (b.items && b.items.some((x) => x.trim()))),
      publish,
    };
    try {
      const res = editing ? await updateNews(id, payload) : await createNews(payload);
      navigate(`/app/news/${res.id || id}`);
    } catch (e) {
      setErr(e.message || "Ошибка сохранения");
    } finally { setBusy(false); }
  }

  return (
    <div className="nws-page nws-page--article">
      <header className="nws-topbar">
        <div className="nws-topbar-inner">
          <Link to="/app/news" className="nws-back">← Отмена</Link>
          <strong style={{ fontSize: 16 }}>{editing ? "Редактировать" : "Новая новость"}</strong>
        </div>
      </header>

      <div className="nws-container">
        {/* Явное предупреждение вместо тихой пустой формы. */}
        {editing && loadFailed && (
          <div className="nws-action-error" style={{ marginBottom: 16 }}>
            Не удалось загрузить новость. Сохранять сейчас нельзя — иначе
            пустая форма затрёт существующий текст.{" "}
            <Button variant="ghost" size="sm" onClick={() => setLoadTick((t) => t + 1)}>
              Повторить
            </Button>
          </div>
        )}
        {editing && loadingNews && (
          <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
            <Skeleton h={40} />
            <Skeleton h={64} />
          </div>
        )}
        <div className="nws-editor">
          <div>
            <label className="nws-label">Заголовок</label>
            <input className="nws-inp" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Заголовок новости" />
          </div>
          <div>
            <label className="nws-label">Краткое описание (для карточки)</label>
            <textarea className="nws-inp" value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={500} rows={2} />
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <label className="nws-label">Рубрика</label>
              <select className="nws-inp" value={category} onChange={(e) => setCategory(e.target.value)}>
                {/* В <option> SVG вставить нельзя, но и эмодзи не нужны:
                    подпись рубрики понятна сама по себе. */}
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 18 }}>
              <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} /> Закрепить
            </label>
          </div>
          <div>
            <label className="nws-label">Обложка</label>
            {cover && <img src={newsImageUrl(cover)} alt="" style={{ maxWidth: 240, borderRadius: 10, display: "block", marginBottom: 8 }} />}
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0], setCover)} />
          </div>

          <label className="nws-label" style={{ marginTop: 12 }}>Содержание</label>
          {blocks.map((b, i) => (
            <div className="nws-block" key={i}>
              {b.type === "paragraph" && <textarea className="nws-inp" rows={3} placeholder="Текст параграфа…" value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />}
              {b.type === "heading" && <input className="nws-inp" placeholder="Заголовок раздела" value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />}
              {b.type === "quote" && <textarea className="nws-inp" rows={2} placeholder="Цитата" value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />}
              {b.type === "list" && (
                <div>
                  {(b.items || []).map((it, k) => (
                    <input key={k} className="nws-inp" style={{ marginBottom: 6 }} placeholder={`Пункт ${k + 1}`} value={it}
                           onChange={(e) => setBlock(i, { items: b.items.map((x, m) => (m === k ? e.target.value : x)) })} />
                  ))}
                  <button className="nws-add-btn" onClick={() => setBlock(i, { items: [...(b.items || []), ""] })}>+ пункт</button>
                </div>
              )}
              {b.type === "image" && (
                <div>
                  {b.url && <img src={newsImageUrl(b.url)} alt="" style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 8 }} />}
                  <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0], (url) => setBlock(i, { url }))} />
                  <input className="nws-inp" style={{ marginTop: 6 }} placeholder="Подпись (опц.)" value={b.caption || ""} onChange={(e) => setBlock(i, { caption: e.target.value })} />
                </div>
              )}
              <div className="nws-block-tools">
                <button className="nws-add-btn" onClick={() => moveBlock(i, -1)}>↑</button>
                <button className="nws-add-btn" onClick={() => moveBlock(i, 1)}>↓</button>
                <button className="nws-add-btn" onClick={() => removeBlock(i)}>✕</button>
              </div>
            </div>
          ))}

          <div className="nws-add-row">
            {BLOCK_TYPES.map((bt) => (
              <button key={bt.type} className="nws-add-btn" onClick={() => addBlock(bt.type)}>+ {bt.label}</button>
            ))}
          </div>

          {err && <div className="nws-err">{err}</div>}
          {uploading && <div className="nws-upload-note">Загружаем картинку…</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
            <Button variant="ghost" loading={busy} onClick={() => save(false)}>
              Сохранить черновик
            </Button>
            <Button variant="primary" loading={busy} onClick={() => save(true)}>
              Опубликовать
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
